import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame, CookingPot, Microwave, Soup, Sandwich, Plus, Check, ArrowUpRight, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrderList } from "@/context/OrderListContext";
import { useSite, trackProductAdd } from "@/lib/api";
import { formatPrice, STATUS_META, TAG_STYLES } from "@/lib/site";
import { Stars } from "@/components/products/MeatIdentityBars";

const METHOD_ICONS = { Mangal: Flame, Tava: CookingPot, Fırın: Microwave, Tencere: Soup, Burger: Sandwich };

export const ProductCard = ({ product }) => {
  const { addItem, isInList } = useOrderList();
  const { data: site } = useSite();
  const inList = isInList(product.slug);
  const statusMeta = STATUS_META[product.status] || STATUS_META.available;
  const waUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba, ${product.name} hakkında bilgi almak istiyorum.`)}`
    : null;

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addItem(product, "1 kg");
    trackProductAdd(product.slug);
    toast.success(`${product.name} sipariş listene eklendi`, {
      description: "Miktarı listeden ayarlayabilirsin.",
    });
  };

  return (
    <div
      data-testid={`product-card-${product.slug}`}
      className="group relative flex flex-col bg-paper border border-dashed border-bordo/25 transition-all duration-300 hover:-translate-y-1 hover:border-bordo/50 hover:shadow-[0_20px_50px_-20px_rgba(122,26,26,0.35)]"
    >
      <Link to={`/urun/${product.slug}`} className="relative block aspect-[5/6] overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {/* kategori şeridi */}
        <span className="absolute left-0 top-4 bg-bordo px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-cream">
          {product.category}
        </span>
        {/* 1985 mührü */}
        <span className="absolute right-3 top-3 flex h-12 w-12 items-center justify-center rounded-full border border-cream/70 bg-charcoal/30 font-mono text-[9px] uppercase leading-none text-cream backdrop-blur-sm">
          Est<br />1985
        </span>
        {product.tags?.length > 0 && (
          <div className="absolute bottom-3 left-3 flex flex-wrap gap-1.5">
            {product.tags.slice(0, 2).map((t) => (
              <span key={t} className="bg-cream/90 px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-bordo">
                {t}
              </span>
            ))}
          </div>
        )}
      </Link>

      <div className="flex flex-1 flex-col p-5">
        {product.fresh_cut && (
          <span
            data-testid={`fresh-cut-badge-${product.slug}`}
            className="mb-2.5 inline-flex w-fit items-center gap-1.5 border border-copper/40 bg-copper/[0.08] px-2 py-1"
          >
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-bordo/70" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-bordo" />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-bordo">Bugün Taze Kesim</span>
          </span>
        )}
        <h3 className="font-serif text-2xl leading-tight text-charcoal">
          <Link to={`/urun/${product.slug}`} className="transition-colors hover:text-bordo">
            {product.name}
          </Link>
        </h3>
        <p className="mt-1.5 text-sm text-[#8C857D] line-clamp-2">{product.short_description}</p>

        <hr className="cut-line my-4" />

        <div className="space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/60">Lezzet</span>
            <Stars value={product.flavor} />
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/60">Yumuşaklık</span>
            <Stars value={product.tenderness} />
          </div>
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/60">Yağ Oranı</span>
            <span className="font-mono text-xs font-bold text-bordo">{product.fat}</span>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-charcoal/45">
            {product.cooking_methods?.slice(0, 3).map((m) => {
              const Icon = METHOD_ICONS[m] || Flame;
              return <Icon key={m} className="h-4 w-4" title={m} />;
            })}
          </div>
          <span className="font-mono text-[11px] uppercase tracking-wider text-copper">{product.difficulty}</span>
        </div>

        {product.price != null && (
          <div className="mt-4 flex items-baseline justify-between border-t border-dashed border-bordo/20 pt-3">
            <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/55">Kg Fiyatı</span>
            <span className="flex items-baseline gap-1">
              <span data-testid={`product-price-${product.slug}`} className="font-mono text-lg font-bold text-bordo">
                {formatPrice(product.price)}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wide text-charcoal/40">/ {product.unit || "kg"}</span>
            </span>
          </div>
        )}

        <div className="mt-4 flex items-center gap-2">
          <button
            data-testid={`add-to-order-${product.slug}`}
            onClick={handleAdd}
            className={cn(
              "inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-sm px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors",
              inList
                ? "bg-charcoal text-cream"
                : "bg-bordo text-cream hover:bg-bordo-dark"
            )}
          >
            {inList ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {inList ? "Listede" : "Listeme Ekle"}
          </button>
          <Link
            to={`/urun/${product.slug}`}
            data-testid={`product-detail-link-${product.slug}`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-sm border border-charcoal/20 text-charcoal transition-colors hover:border-bordo hover:text-bordo"
            title="Detayları İncele"
          >
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </div>

        {waUrl && (
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`whatsapp-ask-${product.slug}`}
            className="mt-2 inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-sm border border-[#25D366] px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#1a7d3c] transition-colors hover:bg-[#25D366] hover:text-charcoal"
          >
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp&apos;tan Sor
          </a>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
